import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { sessionStart, sessionLabel, resolveZone, dateLabel } from '../dateUtils';
import { profileReady } from '../matchList';
import { displayName, examLabel } from '../names';
import { acceptSession, declineSession, offerCalendar, inLiveWindow, SESSION_MS } from '../sessionActions';
import { withoutBlocked, isTestAccount } from '../blocking';
import { startCall } from './VideoCall';
import { useProgress, progressTotals } from '../progress';
import { usePhotos } from '../usePhotos';
import { openInviteSheet } from './ShareSheet';
import { readMeta, saveMeta } from '../privateMeta';
import { confirmSheet } from './feedback';
import { Icon, Avatar } from './ui';

// Today (brief 017): what needs the user now — the next session, requests to
// answer, unread chats, new shared documents and the best matches.
function Today({ user, userProfile, unreadMsgs, pendingReqs, unseenDocs, blocked, matches: allMatches, go, openChat }) {
  const zone = resolveZone(userProfile?.timezone);
  const myName = displayName(userProfile?.name, 'Your study partner');
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const listen = (field, isMine, set) => onSnapshot(
      query(collection(db, 'sessions'), where(field, '==', user.uid)),
      snap => set(snap.docs.map(d => ({ id: d.id, ...d.data(), isMine }))),
      e => console.error(e),
    );
    const u1 = listen('fromUid', true, setSent);
    const u2 = listen('toUid', false, setReceived);
    return () => { u1(); u2(); };
  }, [user.uid]);

  const progress = useProgress(user.uid);
  const ready = profileReady(userProfile);
  // Loaded by Dashboard (shared with Matches); null until the first load.
  const matches = allMatches && withoutBlocked(allMatches, blocked, m => m.uid);

  const now = Date.now();
  const upcoming = withoutBlocked([...sent, ...received], blocked, s => s.isMine ? s.toUid : s.fromUid)
    .filter(s => s.status === 'accepted')
    .map(s => ({ s, t: sessionStart(s)?.getTime() }))
    .filter(({ t }) => t != null && t + SESSION_MS > now)
    .sort((a, b) => a.t - b.t)
    .map(({ s }) => s);
  const next = upcoming[0];

  const requests = pendingReqs
    .map(r => ({ ...r, isMine: false }))
    .filter(r => { const t = sessionStart(r)?.getTime(); return t == null || t + SESSION_MS > now; })
    .sort((a, b) => (sessionStart(a)?.getTime() || 0) - (sessionStart(b)?.getTime() || 0));

  // One row per person, newest message first.
  const chats = useMemo(() => {
    const by = new Map();
    [...unreadMsgs]
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
      .forEach(m => {
        const c = by.get(m.fromUid);
        if (c) c.count++;
        else by.set(m.fromUid, { uid: m.fromUid, name: displayName(m.fromName), text: m.content || '', count: 1 });
      });
    return [...by.values()];
  }, [unreadMsgs]);

  // Sessions don't carry photos; read them for the people shown here (brief 029).
  const photoOf = usePhotos([next && (next.isMine ? next.toUid : next.fromUid), ...requests.map(r => r.fromUid)]);

  // Once, after the first accepted session (P-1): suggest inviting someone else.
  // Not for E2E test accounts, so the automated run isn't interrupted.
  const hasAccepted = [...sent, ...received].some(s => s.status === 'accepted');
  const promptChecked = useRef(false);
  useEffect(() => {
    if (!hasAccepted || promptChecked.current || isTestAccount(userProfile)) return;
    promptChecked.current = true;
    (async () => {
      const meta = await readMeta(user.uid).catch(() => null);
      if (!meta || meta.invitePromptShown) return;
      await saveMeta(user.uid, { invitePromptShown: true }).catch(() => {});
      const yes = await confirmSheet({
        title: 'Session booked',
        message: `Know someone else studying for ${examLabel(userProfile?.studyCategory) || 'your exam'}? Invite them, and they'll find partners too.`,
        confirmLabel: 'Invite', cancelLabel: 'Not now',
      });
      if (yes) openInviteSheet(user.uid, userProfile);
    })();
  }, [hasAccepted, user.uid, userProfile]);

  const answer = async (r, accept) => {
    setBusy(r.id);
    if (accept) await acceptSession(r, { uid: user.uid, myName, zone });
    else await declineSession(r, { uid: user.uid, myName });
    setBusy(null);
  };

  const top = (matches || []).slice(0, 3);
  const nothing = !next && !requests.length && !chats.length && !unseenDocs && matches && !top.length;
  const first = displayName(userProfile?.name, '');

  return (
    <>
      <p className="page-sub">{first ? `Welcome back, ${first}.` : 'Welcome back.'}</p>

      {/* Two columns on wide screens (review R9): what's planned on the left, people on the right. */}
      <div className="today-grid">
        <div className="today-col">
        {next && (() => {
          const partner = displayName(next.isMine ? next.toName : next.fromName);
          const live = inLiveWindow(next, now);
          return (
            <div className="card today-card">
              <h2 className="card-label">Next session</h2>
              <div className="today-next">
                <Avatar src={photoOf(next.isMine ? next.toUid : next.fromUid)} name={partner} size={48} viewable />
                <div className="today-row-main">
                  <div className="today-next-name">{partner}</div>
                  <div className="today-row-sub">{sessionLabel(next, zone)}{(next.subjects || []).length > 0 && ` · ${next.subjects.join(', ')}`}</div>
                </div>
              </div>
              <div className="today-actions">
                {live
                  ? <button className="btn" onClick={() => startCall(next.isMine ? next.toUid : next.fromUid, partner)}><Icon name="video" size={20} />Join video</button>
                  : <button className="btn-small secondary" onClick={() => offerCalendar(next, zone)}><Icon name="calendar" size={18} />Add to calendar</button>}
                {upcoming.length > 1 && <button className="btn-text" onClick={() => go('sessions')}>{upcoming.length - 1} more upcoming</button>}
              </div>
            </div>
          );
        })()}

        {requests.length > 0 && (
          <div className="card today-card">
            <h2 className="card-label">Requests ({requests.length})</h2>
            {requests.map(r => (
              <div key={r.id} className="today-row">
                <Avatar src={photoOf(r.fromUid)} name={displayName(r.fromName)} size={40} viewable />
                <div className="today-row-main">
                  <div className="today-row-title">{displayName(r.fromName)}</div>
                  <div className="today-row-sub">{sessionLabel(r, zone)}</div>
                </div>
                <div className="today-row-actions">
                  <button className="btn-small accent-outline" disabled={busy === r.id} onClick={() => answer(r, true)}>Accept</button>
                  <button className="btn-small btn-decline" disabled={busy === r.id} onClick={() => answer(r, false)}>Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {progress?.length > 0 && (() => {
          const t = progressTotals(progress);
          return (
            <div className="card today-card today-progress">
              <h2 className="card-label">Progress</h2>
              <p className="progress-summary">{t.sessions} {t.sessions === 1 ? 'session' : 'sessions'} · {t.hours} h studied</p>
              {progress.slice(0, 2).map(e => (
                <button key={e.id} className="today-row today-link" onClick={() => go('progress')}>
                  <div className="today-row-main">
                    <div className="today-row-title">{e.title}</div>
                    <div className="today-row-sub">{e.partner} · {dateLabel(e.start, zone)}</div>
                  </div>
                  <Icon name="chevron" size={20} />
                </button>
              ))}
              <button className="btn-text today-more" onClick={() => go('progress')}>See all</button>
            </div>
          );
        })()}
        </div>
        <div className="today-col">
        {chats.length > 0 && (
          <div className="card today-card">
            <h2 className="card-label">Unread chats</h2>
            {chats.slice(0, 3).map(c => (
              <button key={c.uid} className="today-row today-link" onClick={() => openChat(c.uid, c.name)}>
                <Avatar name={c.name} size={40} />
                <div className="today-row-main">
                  <div className="today-row-title">{c.name}</div>
                  <div className="today-row-sub">{c.text}</div>
                </div>
                {c.count > 1 && <span className="badge">{c.count}</span>}
                <Icon name="chevron" size={20} />
              </button>
            ))}
            {chats.length > 3 && <button className="btn-text today-more" onClick={() => go('messages')}>All messages</button>}
          </div>
        )}

        {unseenDocs > 0 && (
          <button className="card row-link" onClick={() => go('materials')}>
            <Icon name="docs" />
            <span className="row-link-text">{unseenDocs} new shared document{unseenDocs > 1 ? 's' : ''}</span>
            <Icon name="chevron" />
          </button>
        )}

        {top.length > 0 && (
          <div className="card today-card">
            <h2 className="card-label">Top matches</h2>
            {top.map(m => (
              // A div, so the photo can be its own button inside the row.
              <div key={m.uid} className="today-row today-link" onClick={() => go('matches')}>
                <Avatar src={m.profilePicture} name={displayName(m.name)} size={40} viewable />
                <button type="button" className="today-row-main today-row-open" onClick={e => { e.stopPropagation(); go('matches'); }}>
                  <span className="today-row-title">{displayName(m.name)}</span>
                  <span className="today-row-sub">{m.canTeach.length ? `Teaches you ${m.canTeach.slice(0, 2).join(', ')}` : `${m.sharedSlots} shared time slots`}</span>
                </button>
                <span className="today-score">{m.score}%</span>
              </div>
            ))}
            <button className="btn-text today-more" onClick={() => go('matches')}>See all matches</button>
          </div>
        )}
        </div>
      </div>

      {matches === null && !next && !requests.length && !chats.length && (
        <div className="card today-card"><div className="skeleton-row" /><div className="skeleton-row" /></div>
      )}

      {nothing && (
        <div className="card empty-state">
          <div className="empty-icon"><Icon name="matches" size={40} /></div>
          <h3>{ready ? 'Find your first study partner' : 'Finish your profile'}</h3>
          <p className="small">{ready ? 'See who studies your subjects at the same hours as you.' : 'Add your exam, subjects and free hours to see your matches.'}</p>
          <div className="empty-actions">
            <button className="btn-small secondary" onClick={() => go(ready ? 'matches' : 'onboarding')}>{ready ? 'Find a partner' : 'Complete profile'}</button>
            {ready && <button className="btn-small secondary" onClick={() => openInviteSheet(user.uid, userProfile)}>Invite a partner</button>}
          </div>
        </div>
      )}
    </>
  );
}
export default Today;
